import { CalendarInfo, Promotion } from "./types"

/** Most frequent non-empty value (ties broken by first occurrence). */
export function most_common_value(values: Array<string | undefined>): string | undefined {
  const counts = new Map<string, number>()
  for (const value of values) {
    const trimmed = value?.trim()
    if (!trimmed) continue
    counts.set(trimmed, (counts.get(trimmed) || 0) + 1)
  }
  let best: string | undefined
  let best_count = 0
  for (const [value, count] of counts) {
    if (count > best_count) {
      best = value
      best_count = count
    }
  }
  return best
}

/**
 * Fields copied onto a new promotion when a planning calendar is selected.
 *
 * Verified on prod (sugobrands, 2026-07-13): a calendar's promotions all share one
 * (customer, retailer) pair, and the retailer matches `calendar.retailer_name` — but the
 * customer is often a *different* party, the distributor billing the deal (the "Big Y Foods"
 * calendar bills through Bozzuto's; "Tops" through C&S - Lancaster; "Key Food Stores" through
 * UNFI - Schnecksville). So the calendar's existing promotions are the source of truth;
 * `retailer_name` is only a fallback for the retailer, and a calendar with no promotions yet
 * leaves the customer blank for the user to pick explicitly (guessing "Direct" was wrong for
 * roughly half of Sugo's calendars).
 */
export function promotion_fields_from_calendar(
  calendar: Pick<CalendarInfo, "retailer_name" | "brand_name" | "calendar_year" | "promotions" | "customer_name"> | undefined,
): Pick<Promotion, "customer" | "retailer" | "brand" | "fiscal_year"> {
  if (!calendar) return {}
  const promotions = calendar.promotions ?? []
  return {
    // A calendar created with a customer (the form now asks) fills it on an otherwise empty calendar.
    customer: most_common_value(promotions.map(promotion => promotion.customer)) || calendar.customer_name || "",
    retailer: most_common_value(promotions.map(promotion => promotion.retailer)) || calendar.retailer_name || "",
    brand: most_common_value(promotions.map(promotion => promotion.brand)) || calendar.brand_name || "",
    fiscal_year: calendar.calendar_year,
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function calendar(overrides: Partial<CalendarInfo>): CalendarInfo {
    return {
      id: "cal-1",
      calendar_name: "Test Calendar",
      source_type: "manual",
      promotions: [],
      created_at: "2026-01-01T00:00:00Z",
      ...overrides,
    }
  }

  describe("most_common_value", () => {
    it("returns the most frequent non-empty value", () => {
      expect(most_common_value(["a", "b", "b", undefined, " "])).toBe("b")
      expect(most_common_value([undefined, ""])).toBeUndefined()
    })
  })

  describe("promotion_fields_from_calendar", () => {
    it("takes customer and retailer from the calendar's promotions, not retailer_name", () => {
      // Real prod shape: the Big Y Foods calendar bills through Bozzuto's.
      expect(
        promotion_fields_from_calendar(
          calendar({
            retailer_name: "Big Y Foods",
            brand_name: "Sunrise",
            calendar_year: 2026,
            promotions: [
              { name: "P1", products: [], customer: "Bozzuto's", retailer: "Big Y Foods" },
              { name: "P2", products: [], customer: "Bozzuto's", retailer: "Big Y Foods" },
            ],
          })
        )
      ).toEqual({ customer: "Bozzuto's", retailer: "Big Y Foods", brand: "Sunrise", fiscal_year: 2026 })
    })

    it("falls back to retailer_name for the retailer but leaves customer blank on an empty calendar", () => {
      expect(
        promotion_fields_from_calendar(calendar({ retailer_name: "Bozzuto's", brand_name: "Sunrise" }))
      ).toEqual({ customer: "", retailer: "Bozzuto's", brand: "Sunrise", fiscal_year: undefined })
    })

    it("takes the calendar's own customer when it has no promotions yet", () => {
      expect(promotion_fields_from_calendar(calendar({ customer_name: "KeHE", retailer_name: "Sprouts" }))).toEqual({
        customer: "KeHE",
        retailer: "Sprouts",
        brand: "",
        fiscal_year: undefined,
      })
    })

    it("returns nothing for a missing calendar", () => {
      expect(promotion_fields_from_calendar(undefined)).toEqual({})
    })
  })
}
